import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  TrendingUp,
  Description,
  MonetizationOn,
  Info,
  CloudUpload,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface FundingOpportunity {
  id: string;
  title: string;
  min_amount: number;
  max_amount: number;
  interest_rate_range: string;
  term_months: number;
  eligibility_criteria: string[];
  description: string;
}

interface LoanApplication {
  business_name: string;
  business_plan_id?: string;
  loan_amount: number;
  loan_purpose: string;
  business_structure: string;
  years_in_business: number;
  annual_revenue: number;
  credit_score?: number;
  contact_email: string;
  contact_phone: string;
}

const FundingCirclePage: React.FC = () => {
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [requirementsDialogOpen, setRequirementsDialogOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<FundingOpportunity | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<LoanApplication>({
    business_name: '',
    loan_amount: 50000,
    loan_purpose: '',
    business_structure: 'LLC',
    years_in_business: 2,
    annual_revenue: 100000,
    contact_email: '',
    contact_phone: '',
  });

  // Fetch status
  const { data: status } = useQuery({
    queryKey: ['funding-circle-status'],
    queryFn: async () => {
      const response = await api.get('/funding-circle/status');
      return response.data;
    },
  });

  // Fetch opportunities
  const { data: opportunities, isLoading } = useQuery({
    queryKey: ['funding-opportunities'],
    queryFn: async () => {
      const response = await api.get('/funding-circle/opportunities');
      return response.data as FundingOpportunity[];
    },
  });

  // Fetch requirements
  const { data: requirements } = useQuery({
    queryKey: ['funding-requirements'],
    queryFn: async () => {
      const response = await api.get('/funding-circle/requirements');
      return response.data;
    },
  });

  // Submit application mutation
  const applyMutation = useMutation({
    mutationFn: async (data: LoanApplication) => {
      return api.post('/funding-circle/applications', data);
    },
    onSuccess: (response) => {
      alert(
        `Application Submitted!\n\nApplication ID: ${response.data.application_id}\nStatus: ${response.data.status}\n\n${response.data.message}`
      );
      setApplyDialogOpen(false);
      setActiveStep(0);
      setFormData({
        business_name: '',
        loan_amount: 50000,
        loan_purpose: '',
        business_structure: 'LLC',
        years_in_business: 2,
        annual_revenue: 100000,
        contact_email: '',
        contact_phone: '',
      });
    },
    onError: (error: any) => {
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleApply = (opportunity: FundingOpportunity) => {
    setSelectedOpportunity(opportunity);
    setFormData({
      ...formData,
      loan_amount: opportunity.min_amount,
    });
    setApplyDialogOpen(true);
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    applyMutation.mutate(formData);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const steps = ['Business Information', 'Loan Details', 'Contact Information', 'Review & Submit'];

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <AccountBalance sx={{ fontSize: 48, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Funding Circle</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Business Loans & Financing Solutions
            </Typography>
          </Box>
        </Box>

        {/* Status Alert */}
        {status && (
          <Alert
            severity={status.api_configured ? 'success' : 'info'}
            icon={status.api_configured ? <CheckCircle /> : <Info />}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {status.message}
              </Typography>
              <Typography variant="caption">
                Environment: {status.environment} | Status:{' '}
                {status.connected ? 'Connected' : 'Not Connected'}
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Quick Actions */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<Info />}
            onClick={() => setRequirementsDialogOpen(true)}
          >
            View Requirements
          </Button>
          <Button
            variant="outlined"
            startIcon={<Description />}
            href="/business-plans"
          >
            My Business Plans
          </Button>
        </Box>
      </Box>

      {/* Funding Opportunities */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Available Funding Options
      </Typography>

      <Grid container spacing={3}>
        {opportunities?.map((opportunity) => (
          <Grid item xs={12} md={6} key={opportunity.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <MonetizationOn color="primary" fontSize="large" />
                  <Typography variant="h6">{opportunity.title}</Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {opportunity.description}
                </Typography>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Loan Amount Range
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(opportunity.min_amount)} -{' '}
                    {formatCurrency(opportunity.max_amount)}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Chip
                    icon={<TrendingUp />}
                    label={opportunity.interest_rate_range}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`${opportunity.term_months} months max`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Eligibility Criteria:
                </Typography>
                <List dense>
                  {opportunity.eligibility_criteria.slice(0, 3).map((criteria, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={criteria}
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={() => handleApply(opportunity)}
                >
                  Apply Now
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Application Dialog */}
      <Dialog
        open={applyDialogOpen}
        onClose={() => setApplyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Apply for {selectedOpportunity?.title}
          <Typography variant="caption" display="block" color="text.secondary">
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Business Information */}
          {activeStep === 0 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Business Name"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                select
                label="Business Structure"
                value={formData.business_structure}
                onChange={(e) =>
                  setFormData({ ...formData, business_structure: e.target.value })
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="LLC">LLC</MenuItem>
                <MenuItem value="Corporation">Corporation</MenuItem>
                <MenuItem value="S-Corp">S-Corporation</MenuItem>
                <MenuItem value="Partnership">Partnership</MenuItem>
                <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
              </TextField>
              <TextField
                fullWidth
                type="number"
                label="Years in Business"
                value={formData.years_in_business}
                onChange={(e) =>
                  setFormData({ ...formData, years_in_business: parseInt(e.target.value) })
                }
                sx={{ mb: 2 }}
                inputProps={{ min: 0 }}
                required
              />
              <TextField
                fullWidth
                type="number"
                label="Annual Revenue (£)"
                value={formData.annual_revenue}
                onChange={(e) =>
                  setFormData({ ...formData, annual_revenue: parseFloat(e.target.value) })
                }
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 1000 }}
                required
              />
              <TextField
                fullWidth
                type="number"
                label="Credit Score (Optional)"
                value={formData.credit_score || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credit_score: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                inputProps={{ min: 300, max: 850 }}
                helperText="Your personal or business credit score (300-850)"
              />
            </Box>
          )}

          {/* Step 2: Loan Details */}
          {activeStep === 1 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Loan Amount (£)"
                value={formData.loan_amount}
                onChange={(e) =>
                  setFormData({ ...formData, loan_amount: parseFloat(e.target.value) })
                }
                sx={{ mb: 2 }}
                inputProps={{
                  min: selectedOpportunity?.min_amount,
                  max: selectedOpportunity?.max_amount,
                  step: 1000,
                }}
                required
                helperText={`Range: ${formatCurrency(
                  selectedOpportunity?.min_amount || 0
                )} - ${formatCurrency(selectedOpportunity?.max_amount || 0)}`}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Loan Purpose"
                value={formData.loan_purpose}
                onChange={(e) => setFormData({ ...formData, loan_purpose: e.target.value })}
                placeholder="Describe how you will use the loan funds..."
                required
                helperText="Be specific about your plans for the loan"
              />

              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Estimated Monthly Payment
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {formatCurrency(formData.loan_amount * 0.02)} / month
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Based on {selectedOpportunity?.term_months} month term (estimate only)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Step 3: Contact Information */}
          {activeStep === 2 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                type="email"
                label="Contact Email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                type="tel"
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="(555) 123-4567"
                required
                helperText="We'll contact you at this number for verification"
              />
            </Box>
          )}

          {/* Step 4: Review */}
          {activeStep === 3 && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review your application before submitting
              </Alert>

              <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Business Information
                </Typography>
                <Typography variant="body2">Name: {formData.business_name}</Typography>
                <Typography variant="body2">
                  Structure: {formData.business_structure}
                </Typography>
                <Typography variant="body2">
                  Years in Business: {formData.years_in_business}
                </Typography>
                <Typography variant="body2">
                  Annual Revenue: {formatCurrency(formData.annual_revenue)}
                </Typography>
                {formData.credit_score && (
                  <Typography variant="body2">Credit Score: {formData.credit_score}</Typography>
                )}
              </Paper>

              <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Loan Details
                </Typography>
                <Typography variant="body2">
                  Amount: {formatCurrency(formData.loan_amount)}
                </Typography>
                <Typography variant="body2">Purpose: {formData.loan_purpose}</Typography>
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Contact Information
                </Typography>
                <Typography variant="body2">Email: {formData.contact_email}</Typography>
                <Typography variant="body2">Phone: {formData.contact_phone}</Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
          {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={applyMutation.isPending}
            >
              {applyMutation.isPending ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Requirements Dialog */}
      <Dialog
        open={requirementsDialogOpen}
        onClose={() => setRequirementsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Funding Circle Requirements</DialogTitle>
        <DialogContent>
          {requirements && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                General Requirements
              </Typography>
              <List>
                {Object.entries(requirements.general_requirements).map(([key, value]) => (
                  <ListItem key={key}>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={String(value)} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Required Documents
              </Typography>
              <List>
                {requirements.required_documents.map((doc: string, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <Description color="action" />
                    </ListItemIcon>
                    <ListItemText primary={doc} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Process Timeline
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                {Object.entries(requirements.process_timeline).map(([key, value]) => (
                  <Box key={key} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="body2">{String(value)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequirementsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FundingCirclePage;
